import Button from '@digitalshared/button';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ApiService } from '../../js/ApiService';
import useCooldown from '../util/useCooldown/useCooldown';

const CheckYourInbox = () => {
  const history = useHistory();
  const { state } = useLocation();
  const {
    email = '',
    isMagicLink = false,
    magicLinkPayload = null,
    initialCooldown = 0,
    // Set when a new POS registration link was requested from an expired one
    isRegistrationLink = false,
    registrationToken = ''
  } = state || {};
  const hasCooldownLink = isMagicLink || isRegistrationLink;
  const linkSent = isRegistrationLink
    ? 'We sent a new link to'
    : 'We sent a secure sign-in link to';
  const linkAction = isRegistrationLink
    ? 'Tap the link to finish setting up your STATUS account.'
    : 'Tap the link to verify your email and instantly sign in to your STATUS account.';
  const linkExpiry = isMagicLink ? '15 minutes' : '24 hours';

  const [resendLoading, setResendLoading] = useState(false);
  const { cooldown, startCooldown } = useCooldown(initialCooldown);
  const [message, setMessage] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleRequestNewRegistrationLink = async () => {
    // The expired link's token is the only way to ask for a new registration link
    if (!registrationToken) {
      setErrorMsg('Cannot request a new link. Please use the link from your email again.');
      return;
    }

    try {
      setResendLoading(true);
      setErrorMsg('');
      setMessage('');

      const response = await ApiService.resendMagicRegistrationLink(registrationToken);

      if (response.status === 200) {
        setMessage('A new link has been sent to your email.');
        startCooldown(30);
      } else if (response.status === 429) {
        setErrorMsg('Too many link requests. Please try again later.');
        startCooldown(30);
      } else {
        setErrorMsg('Failed to request a new link. Please try again.');
      }
    } catch (error) {
      console.error('Error requesting new registration link', error);
      setErrorMsg('Something went wrong. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleRequestNewLink = async e => {
    e.preventDefault();
    if (resendLoading || cooldown > 0) return;

    if (isRegistrationLink) {
      await handleRequestNewRegistrationLink();
      return;
    }

    if (!isMagicLink || !magicLinkPayload) {
      setErrorMsg(
        'Cannot request new link without initial login parameters. Please try logging in again.'
      );
      return;
    }

    try {
      setResendLoading(true);
      setErrorMsg('');
      setMessage('');

      const response = await ApiService.requestMagicLink(magicLinkPayload);

      if (response.status === 200) {
        setMessage('A new secure sign-in link has been sent to your email.');
        startCooldown(30); // 30 second spam prevention cooldown
      } else if (response.status === 429) {
        setErrorMsg('Too many magic link requests. Please try again later.');
      } else {
        setErrorMsg('Failed to request a new link. Please try again.');
      }
    } catch (error) {
      console.error('Error requesting magic link', error);
      setErrorMsg('Something went wrong. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="body bg-white" data-testid="check-your-inbox">
      <div className="container py-5">
        <div className="mx-auto text-center" style={{ maxWidth: '420px' }}>
          <h1 className="h1 text-uppercase mb-4">CHECK YOUR INBOX FOR A LINK</h1>
          <div className="body2 mt-4 text-muted" data-testid="inbox-instruction">
            {linkSent}{' '}
            {email ? <strong data-testid="recipient-email">{email}</strong> : 'your email address'}.{' '}
            {linkAction} Link expires in {linkExpiry}.
          </div>

          {message && (
            <div
              className="text-success mt-3"
              data-testid="success-msg"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              {message}
            </div>
          )}
          {errorMsg && (
            <div
              className="text-danger mt-3"
              data-testid="error-msg"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              {errorMsg}
            </div>
          )}

          <div className="mt-5 d-flex flex-column align-items-center" style={{ gap: '1rem' }}>
            {hasCooldownLink && (
              <button
                data-testid="smart-link"
                onClick={handleRequestNewLink}
                disabled={resendLoading || cooldown > 0}
                className="link font-weight-bold"
                style={{
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  cursor: resendLoading || cooldown > 0 ? 'not-allowed' : 'pointer',
                  opacity: resendLoading || cooldown > 0 ? 0.6 : 1
                }}
              >
                {resendLoading
                  ? 'Requesting...'
                  : cooldown > 0
                    ? `Resend Link (${cooldown}s)`
                    : 'Request New Link'}
              </button>
            )}

            {!hasCooldownLink && (
              <button
                data-testid="smart-link"
                onClick={handleRequestNewLink}
                className="link font-weight-bold"
                style={{ fontSize: '14px' }}
              >
                Request New Link
              </button>
            )}

            {/* A POS customer finishing registration has no password to sign in with yet */}
            {!isRegistrationLink && (
              <>
                <div className="d-flex align-items-center w-100 my-2">
                  <div className="flex-grow-1 border-top"></div>
                  <span className="mx-2 body2 text-muted text-uppercase">Or</span>
                  <div className="flex-grow-1 border-top"></div>
                </div>
                <Button
                  category="secondary"
                  className="btn btn-block btn-secondary-rounded"
                  onClick={() => history.push('/login')}
                  data-testid="signin-with-password"
                >
                  Sign In With Password
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckYourInbox;
